import React, { useState, useEffect, useCallback, useRef } from "react";

interface Monster {
  id: string;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  damage: number;
  speed: number;
  value: number;
  angle: number;
  level: number;
  brand: "samsung" | "motorola" | "xiaomi";
}

interface Projectile {
  id: string;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  damage: number;
}

interface Particle {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

const TowerDefenseGame = () => {
  const [gold, setGold] = useState(100);
  const [wave, setWave] = useState(1);
  const [towerHealth, setTowerHealth] = useState(100);
  const [towerMaxHealth, setTowerMaxHealth] = useState(100);
  const [towerDamage, setTowerDamage] = useState(10);
  const [fireRate, setFireRate] = useState(1000);
  const [monsters, setMonsters] = useState<Monster[]>([]);
  const [projectiles, setProjectiles] = useState<Projectile[]>([]);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [totalKills, setTotalKills] = useState(0);
  const [gameSpeed, setGameSpeed] = useState(1);

  const lastFireTime = useRef(0);
  const lastSpawnTime = useRef(0);
  type Interval = ReturnType<typeof setInterval>;
  const gameLoopRef = useRef<Interval | null>(null);
  const centerX = 200;
  const centerY = 200;

  const upgradeCosts = {
    damage: Math.floor(50 * Math.pow(1.5, Math.floor(towerDamage / 10))),
    health: Math.floor(75 * Math.pow(1.4, Math.floor(towerMaxHealth / 100))),
    fireRate: Math.floor(
      100 * Math.pow(1.6, Math.floor((1000 - fireRate) / 100))
    ),
  };

  const brands: Array<"samsung" | "motorola" | "xiaomi"> = [
    "samsung",
    "motorola",
    "xiaomi",
  ];

  const createParticles = (
    x: number,
    y: number,
    color: string,
    count: number = 10
  ) => {
    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      newParticles.push({
        id: `particle-${Date.now()}-${i}`,
        x,
        y,
        vx: Math.cos(angle) * 2,
        vy: Math.sin(angle) * 2,
        life: 1,
        color,
      });
    }
    setParticles((prev) => [...prev, ...newParticles]);
  };

  const spawnMonster = useCallback(() => {
    const angle = Math.random() * Math.PI * 2;
    const distance = 250;
    const monsterLevel = Math.floor(wave / 3) + 1;
    const healthMultiplier = Math.pow(1.2, monsterLevel - 1);
    const valueMultiplier = Math.pow(1.3, monsterLevel - 1);
    const randomBrand = brands[Math.floor(Math.random() * brands.length)];

    const newMonster: Monster = {
      id: `monster-${Date.now()}-${Math.random()}`,
      x: centerX + Math.cos(angle) * distance,
      y: centerY + Math.sin(angle) * distance,
      health: Math.floor(20 * healthMultiplier),
      maxHealth: Math.floor(20 * healthMultiplier),
      damage: 5 + monsterLevel,
      speed: 0.5 + monsterLevel * 0.1,
      value: Math.floor(10 * valueMultiplier),
      angle,
      level: monsterLevel,
      brand: randomBrand,
    };

    setMonsters((prev) => [...prev, newMonster]);
  }, [wave]);

  const fireProjectile = useCallback(
    (target: Monster) => {
      const projectile: Projectile = {
        id: `proj-${Date.now()}-${Math.random()}`,
        x: centerX,
        y: centerY,
        targetX: target.x,
        targetY: target.y,
        damage: towerDamage,
      };
      setProjectiles((prev) => [...prev, projectile]);
    },
    [towerDamage]
  );

  const upgradeTower = (type: "damage" | "health" | "fireRate") => {
    if (gold >= upgradeCosts[type]) {
      setGold((prev) => prev - upgradeCosts[type]);

      if (type === "damage") {
        setTowerDamage((prev) => prev + 5);
      } else if (type === "health") {
        setTowerMaxHealth((prev) => prev + 50);
        setTowerHealth((prev) => prev + 50);
      } else if (type === "fireRate" && fireRate > 200) {
        setFireRate((prev) => prev - 100);
      }
    }
  };

  const cycleGameSpeed = () => {
    setGameSpeed((prev) => {
      if (prev === 1) return 2;
      if (prev === 2) return 3;
      return 1;
    });
  };

  const resetGame = () => {
    setGold(100);
    setWave(1);
    setTowerHealth(100);
    setTowerMaxHealth(100);
    setTowerDamage(10);
    setFireRate(1000);
    setMonsters([]);
    setProjectiles([]);
    setParticles([]);
    setGameOver(false);
    setTotalKills(0);
    setGameSpeed(1);
  };

  const renderMonsterLogo = (monster: Monster) => {
    const baseX = monster.x;
    const baseY = monster.y;

    switch (monster.brand) {
      case "samsung":
        return (
          <g key={`logo-${monster.id}`}>
            <ellipse
              cx={baseX}
              cy={baseY}
              rx="18"
              ry="12"
              fill="#1428A0"
              stroke="white"
              strokeWidth="1"
            />
            <text
              x={baseX}
              y={baseY + 3}
              textAnchor="middle"
              fill="white"
              fontSize="8"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              SAMSUNG
            </text>
          </g>
        );
      case "motorola":
        return (
          <g key={`logo-${monster.id}`}>
            <circle
              cx={baseX}
              cy={baseY}
              r="15"
              fill="#003DA5"
              stroke="white"
              strokeWidth="1"
            />
            <text
              x={baseX}
              y={baseY + 6}
              textAnchor="middle"
              fill="white"
              fontSize="20"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              M
            </text>
          </g>
        );
      case "xiaomi":
        return (
          <g key={`logo-${monster.id}`}>
            <rect
              x={baseX - 15}
              y={baseY - 15}
              width="30"
              height="30"
              rx="6"
              fill="#FF6900"
              stroke="white"
              strokeWidth="1"
            />
            <text
              x={baseX}
              y={baseY + 2}
              textAnchor="middle"
              fill="white"
              fontSize="12"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              MI
            </text>
          </g>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (gameOver || isPaused) return;

    const gameLoop = () => {
      const now = Date.now();
      const speedMultiplier = gameSpeed;

      // Spawn monsters
      if (
        now - lastSpawnTime.current >
        Math.max(1000, 3000 - wave * 50) / speedMultiplier
      ) {
        spawnMonster();
        lastSpawnTime.current = now;
      }

      // Move monsters
      setMonsters(
        (prev) =>
          prev
            .map((monster) => {
              const dx = centerX - monster.x;
              const dy = centerY - monster.y;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance < 30) {
                setTowerHealth((health) => {
                  const newHealth = health - monster.damage;
                  if (newHealth <= 0) {
                    setGameOver(true);
                  }
                  return Math.max(0, newHealth);
                });
                createParticles(centerX, centerY, "#ef4444", 5);
                return null;
              }

              return {
                ...monster,
                x:
                  monster.x + (dx / distance) * monster.speed * speedMultiplier,
                y:
                  monster.y + (dy / distance) * monster.speed * speedMultiplier,
              };
            })
            .filter(Boolean) as Monster[]
      );

      // Fire at monsters
      setMonsters((prev) => {
        if (
          prev.length > 0 &&
          now - lastFireTime.current > fireRate / speedMultiplier
        ) {
          const target = prev.reduce((closest, monster) => {
            const dx1 = centerX - monster.x;
            const dy1 = centerY - monster.y;
            const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);

            const dx2 = centerX - closest.x;
            const dy2 = centerY - closest.y;
            const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

            return dist1 < dist2 ? monster : closest;
          });

          fireProjectile(target);
          lastFireTime.current = now;
        }
        return prev;
      });

      // Move projectiles and clean up properly
      setProjectiles((prev) => {
        const updatedProjectiles = prev.map((proj) => {
          const dx = proj.targetX - proj.x;
          const dy = proj.targetY - proj.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 10) {
            // Hit detection
            setMonsters((monsters) => {
              return monsters
                .map((monster) => {
                  const monsterDx = proj.x - monster.x;
                  const monsterDy = proj.y - monster.y;
                  const monsterDist = Math.sqrt(
                    monsterDx * monsterDx + monsterDy * monsterDy
                  );

                  if (monsterDist < 20) {
                    const newHealth = monster.health - proj.damage;
                    if (newHealth <= 0) {
                      setGold((g) => g + monster.value);
                      setTotalKills((k) => k + 1);
                      createParticles(monster.x, monster.y, "#fbbf24", 8);
                      return null;
                    }
                    createParticles(monster.x, monster.y, "#f97316", 3);
                    return { ...monster, health: newHealth };
                  }
                  return monster;
                })
                .filter(Boolean) as Monster[];
            });
            return null; // Remove projectile after hit
          }

          // Check if projectile is out of bounds
          if (proj.x < 0 || proj.x > 400 || proj.y < 0 || proj.y > 400) {
            return null;
          }

          return {
            ...proj,
            x: proj.x + (dx / distance) * 8 * speedMultiplier,
            y: proj.y + (dy / distance) * 8 * speedMultiplier,
          };
        });

        return updatedProjectiles.filter(Boolean) as Projectile[];
      });

      // Update particles
      setParticles((prev) =>
        prev
          .map((particle) => ({
            ...particle,
            x: particle.x + particle.vx * speedMultiplier,
            y: particle.y + particle.vy * speedMultiplier,
            vx: particle.vx * (1 - 0.05 * speedMultiplier),
            vy: particle.vy * (1 - 0.05 * speedMultiplier),
            life: particle.life - 0.02 * speedMultiplier,
          }))
          .filter((p) => p.life > 0)
      );

      // Wave progression
      if (totalKills > wave * 10) {
        setWave((w) => w + 1);
      }
    };

    gameLoopRef.current = setInterval(gameLoop, 1000 / 60);

    return () => {
  if (gameLoopRef.current) {
    clearInterval(gameLoopRef.current);
    gameLoopRef.current = null;
  }
};
  }, [
    gameOver,
    isPaused,
    fireRate,
    spawnMonster,
    fireProjectile,
    totalKills,
    wave,
    gameSpeed,
  ]);

  const getSpeedColor = () => {
    if (gameSpeed === 1) return "bg-blue-600";
    if (gameSpeed === 2) return "bg-yellow-600";
    return "bg-red-600";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-white mb-2">
            OnePlus Tower Defense
          </h1>
          <div className="flex justify-center gap-4 text-white">
            <div className="bg-black bg-opacity-30 px-4 py-2 rounded-lg text-center">
              <div className="text-yellow-400 font-bold">💰 Gold:</div>
              <div className="text-xl">{gold}</div>
            </div>
            <div className="bg-black bg-opacity-30 px-4 py-2 rounded-lg text-center">
              <div className="text-purple-400 font-bold">🌊 Wave:</div>
              <div className="text-xl">{wave}</div>
            </div>
            <div className="bg-black bg-opacity-30 px-4 py-2 rounded-lg text-center">
              <div className="text-red-400 font-bold">💀 Kills:</div>
              <div className="text-xl">{totalKills}</div>
            </div>
            <button
              onClick={cycleGameSpeed}
              className={`${getSpeedColor()} hover:opacity-80 px-4 py-2 rounded-lg transition-all transform hover:scale-105 text-center`}
            >
              <div className="font-bold">⚡ Speed:</div>
              <div className="text-xl">{gameSpeed}x</div>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="relative bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl shadow-2xl overflow-hidden">
              <div className="w-full" style={{ aspectRatio: "1/1" }}>
                <svg
                  viewBox="0 0 400 400"
                  preserveAspectRatio="xMidYMid meet"
                  className="w-full h-full"
                >
                  {/* Grid */}
                  <defs>
                    <pattern
                      id="grid"
                      width="40"
                      height="40"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d="M 40 0 L 0 0 0 40"
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="1"
                      />
                    </pattern>
                  </defs>
                  <rect width="400" height="400" fill="url(#grid)" />

                  {/* Particles */}
                  {particles.map((particle) => (
                    <circle
                      key={particle.id}
                      cx={particle.x}
                      cy={particle.y}
                      r={3 * particle.life}
                      fill={particle.color}
                      opacity={particle.life}
                    />
                  ))}

                  {/* Monsters with brand logos */}
                  {monsters.map((monster) => (
                    <g key={monster.id}>
                      {renderMonsterLogo(monster)}
                      {/* Health bar */}
                      <rect
                        x={monster.x - 15}
                        y={monster.y - 25}
                        width="30"
                        height="4"
                        fill="#333"
                      />
                      <rect
                        x={monster.x - 15}
                        y={monster.y - 25}
                        width={30 * (monster.health / monster.maxHealth)}
                        height="4"
                        fill="#ef4444"
                      />
                      {/* Level indicator */}
                      <circle
                        cx={monster.x + 12}
                        cy={monster.y - 12}
                        r="8"
                        fill="#fbbf24"
                        stroke="#000"
                        strokeWidth="1"
                      />
                      <text
                        x={monster.x + 12}
                        y={monster.y - 8}
                        textAnchor="middle"
                        fill="black"
                        fontSize="8"
                        fontWeight="bold"
                      >
                        {monster.level}
                      </text>
                    </g>
                  ))}

                  {/* Projectiles */}
                  {projectiles.map((proj) => (
                    <circle
                      key={proj.id}
                      cx={proj.x}
                      cy={proj.y}
                      r="4"
                      fill="#fbbf24"
                      className="drop-shadow-lg"
                    />
                  ))}

                  {/* OnePlus Tower - Always centered with white background for visibility */}
                  <g>
                    {/* White background circle for logo visibility */}
                    <circle cx={centerX} cy={centerY} r="28" fill="white" />
                    <image
                      href="https://miro.medium.com/v2/resize:fit:720/format:webp/1*nYbKJr9SdqE9AwmpwXYx5w.png"
                      x={centerX - 25}
                      y={centerY - 25}
                      width="50"
                      height="50"
                    />

                    {/* Health bar */}
                    <rect
                      x={centerX - 30}
                      y={centerY + 35}
                      width="60"
                      height="6"
                      fill="#333"
                      rx="3"
                    />
                    <rect
                      x={centerX - 30}
                      y={centerY + 35}
                      width={60 * (towerHealth / towerMaxHealth)}
                      height="6"
                      fill="#10b981"
                      rx="3"
                    />
                  </g>
                </svg>
              </div>
            </div>

            {gameOver && (
              <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-2xl">
                <div className="text-center text-white">
                  <h2 className="text-3xl font-bold mb-4">Game Over!</h2>
                  <p className="mb-2">You reached Wave {wave}</p>
                  <p className="mb-4">Total Kills: {totalKills}</p>
                  <button
                    onClick={resetGame}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-lg font-bold hover:from-purple-600 hover:to-pink-600 transition"
                  >
                    Play Again
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4">Upgrades</h3>

              <div className="space-y-3">
                <button
                  onClick={() => upgradeTower("damage")}
                  disabled={gold < upgradeCosts.damage}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white p-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-red-600 hover:to-orange-600 transition"
                >
                  <div className="flex justify-between items-center">
                    <span>⚔️ Damage: {towerDamage}</span>
                    <span className="text-yellow-300">
                      💰 {upgradeCosts.damage}
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => upgradeTower("health")}
                  disabled={gold < upgradeCosts.health}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white p-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-green-600 hover:to-emerald-600 transition"
                >
                  <div className="flex justify-between items-center">
                    <span>❤️ Health: {towerMaxHealth}</span>
                    <span className="text-yellow-300">
                      💰 {upgradeCosts.health}
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => upgradeTower("fireRate")}
                  disabled={gold < upgradeCosts.fireRate || fireRate <= 200}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-600 hover:to-cyan-600 transition"
                >
                  <div className="flex justify-between items-center">
                    <span>⚡ Fire Rate: {(1000 / fireRate).toFixed(1)}/s</span>
                    <span className="text-yellow-300">
                      💰 {upgradeCosts.fireRate}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4">
                Game Controls
              </h3>

              <button
                onClick={() => setIsPaused(!isPaused)}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition"
              >
                {isPaused ? "▶️ Resume" : "⏸️ Pause"}
              </button>

              <div className="mt-3 text-center">
                <p className="text-xs text-gray-400">
                  Click speed button above to change game speed
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-2">
                Instructions
              </h3>
              <p className="text-gray-300 text-sm">
                Defend your OnePlus tower from rival phone brands! They attack
                from all sides and get stronger over time. Upgrade your tower to
                survive longer waves and earn more gold. Click the speed button
                to play faster!
              </p>
              <p className="text-red-400 font-bold text-sm mt-2">
                Never Settle!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TowerDefenseGame;
